<template>
  <div class="w-full h-full flex flex-col font-sans select-none text-left pl-2 transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-white text-zinc-600'">
    
    <div class="p-5 border-b sticky top-0 z-40 flex xl:flex-row xl:items-center justify-between gap-4 backdrop-blur-md transition-colors duration-200"
      :class="isDark ? 'border-zinc-900 bg-zinc-950/90' : 'border-zinc-200/80 bg-white/90'">

      <div class="flex sm:flex-row sm:items-center gap-4">
        <div class="space-y-0.5">
          <h1 class="text-base font-semibold tracking-tight m-0 transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">Planning</h1>
          <p class="text-xs text-zinc-400 font-medium m-0 capitalize transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ currentPeriodLabel }}</p>
          <!-- Affichage de la date actuelle en mode Journée -->
          <p v-if="calendarViewMode === 'day-team'" class="text-[11px] font-semibold mt-0.5 m-0 transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-700'">
            {{ formatFullDate(currentDate) }}
          </p>
        </div>

        <div class="flex items-center gap-1.5 bg-zinc-100 dark:bg-zinc-900 p-1 rounded-xl border border-zinc-200/40 dark:border-zinc-800/60">
          <button @click="goToPreviousPeriod" class="p-1.5 rounded-lg transition-colors duration-150 hover:bg-white dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button @click="goToToday" class="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 hover:bg-white dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white">
            Aujourd'hui
          </button>
          <button @click="goToNextPeriod" class="p-1.5 rounded-lg transition-colors duration-150 hover:bg-white dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex p-1 rounded-xl border self-start xl:self-auto transition-colors duration-200"
        :class="isDark ? 'bg-zinc-900 border-zinc-800/80' : 'bg-zinc-100 border-zinc-200/60'">
        <button
          type="button"
          @click="calendarViewMode = 'day-team'"
          class="px-4 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer focus:outline-none"
          :class="calendarViewMode === 'day-team' 
            ? (isDark ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-900 shadow-sm border border-zinc-200/40') 
            : (isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-800')"
        >
        Journée
        </button>
        <button
          type="button"
          @click="calendarViewMode = 'week-solo'"
          class="px-4 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer focus:outline-none"
          :class="calendarViewMode === 'week-solo' 
            ? (isDark ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-900 shadow-sm border border-zinc-200/40') 
            : (isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-800')"
        >
        Semaine
        </button>
      </div>

    </div>

    <div v-if="isAdmin" class="px-5 py-2.5 border-b flex flex-wrap items-center gap-2 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900 bg-zinc-950' : 'border-zinc-200/80 bg-white'">

      <template v-if="calendarViewMode === 'day-team'">
        <span class="text-xs font-medium mr-2 transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Équipe :</span>
        <button
          type="button"
          @click="selectAllEmployees"
          class="px-3 py-1 rounded-full border text-xs font-medium transition-all cursor-pointer focus:outline-none"
          :class="selectedEmployeeIds.length === employees.length
            ? (isDark ? 'bg-zinc-900 text-white border-zinc-800' : 'bg-zinc-900 text-white border-zinc-900')
            : (isDark ? 'bg-transparent text-zinc-500 border-transparent hover:text-zinc-300' : 'bg-transparent text-zinc-500 border-transparent hover:text-zinc-800')"
        >
          Tous
        </button>
        <button
          v-for="emp in employees"
          :key="`day-emp-${emp.id}`"
          @click="toggleEmployeeFilter(emp.id)"
          class="px-3 py-1 rounded-full border text-xs font-medium flex items-center gap-2 transition-all cursor-pointer focus:outline-none"
          :class="selectedEmployeeIds.includes(emp.id)
            ? (isDark ? 'bg-zinc-900 text-white border-zinc-800' : 'bg-zinc-100 text-zinc-900 border-zinc-200')
            : (isDark ? 'bg-transparent text-zinc-500 border-transparent hover:text-zinc-300' : 'bg-transparent text-zinc-400 border-transparent hover:text-zinc-800')"
        >
          <span class="w-2 h-2 rounded-full shadow-inner" :style="{ backgroundColor: emp.color }"></span>
          <span>{{ emp.name }}</span>
        </button>
      </template>

      <template v-else>
        <span class="text-xs font-medium mr-2 transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Collaborateur :</span>
        <button
          v-for="emp in employees"
          :key="`week-emp-${emp.id}`"
          @click="activeWeekEmployeeId = emp.id"
          class="px-3 py-1 rounded-full border text-xs font-medium flex items-center gap-2 transition-all cursor-pointer focus:outline-none"
          :class="activeWeekEmployeeId === emp.id
            ? (isDark ? 'bg-zinc-900 text-white border-zinc-700' : 'bg-zinc-100 text-zinc-900 border-zinc-300')
            : (isDark ? 'bg-transparent text-zinc-500 border-transparent hover:text-zinc-300' : 'bg-transparent text-zinc-400 border-transparent hover:text-zinc-800')"
        >
          <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: emp.color }"></span>
          <span>{{ emp.name }}</span>
        </button>
      </template>
    </div>

    <div class="grid grid-cols-[65px_1fr] border-b z-30 shrink-0 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900 bg-zinc-950' : 'border-zinc-200/80 bg-white'">
      <div class="border-r transition-colors duration-200" :class="isDark ? 'border-zinc-900/60 bg-zinc-950' : 'border-zinc-200/60 bg-white'"></div>
      <div class="grid" :class="gridColsClass">
        <div
          v-for="(col, index) in activeColumns"
          :key="`col-header-${index}`"
          class="p-3.5 flex flex-col items-center justify-center text-center gap-0.5 border-l first:border-l-0 transition-colors duration-200"
          :class="isDark ? 'border-zinc-900/40 bg-zinc-950' : 'border-zinc-100 bg-white'"
        >
          <span class="text-xs font-semibold tracking-wide transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">
            {{ col.title }}
          </span>
          <span class="text-[11px] font-medium transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
            {{ col.subtitle }}
          </span>
        </div>
      </div>
    </div>

    <div ref="calendarScrollContainer" class="grid w-full overflow-y-auto max-h-[68vh] relative grid-cols-[65px_1fr] no-scrollbar">
      
      <div class="flex flex-col border-r relative z-10 transition-colors duration-200"
        :class="isDark ? 'border-zinc-900 bg-zinc-950/60' : 'border-zinc-200 bg-white'">
        <div
          v-for="hour in hours"
          :key="`hour-label-${hour}`"
          class="h-[100px] sm:h-[120px] px-2.5 flex items-start justify-end text-[11px] font-medium pt-1.5 border-t first:border-t-0 transition-colors duration-200"
          :class="isDark ? 'border-zinc-900/60 text-zinc-600' : 'border-zinc-100 text-zinc-400'"
        >
          {{ formatHour(hour) }}
        </div>
      </div>

      <div class="grid relative transition-colors duration-200" 
        :class="[gridColsClass, isDark ? 'bg-zinc-950/20' : 'bg-zinc-50/30']">
        
        <div class="absolute left-0 right-0 h-[1px] z-20 flex items-center pointer-events-none shadow-sm" 
          :style="nowIndicatorStyle"
          :class="isDark ? 'bg-amber-400/80' : 'bg-rose-500'">
          <div class="absolute left-[-65px] text-[10px] font-bold px-1.5 py-0.5 rounded shadow-md tracking-wide whitespace-nowrap transition-colors duration-200"
            :class="isDark ? 'bg-zinc-900 text-amber-400 border border-zinc-800' : 'bg-rose-500 text-white'">
            {{ nowLabel }}
          </div>
          <div class="w-2 h-2 rounded-full absolute left-0 -translate-x-1/2 shadow"
            :class="isDark ? 'bg-amber-400' : 'bg-rose-500'"></div>
        </div>

        <div
          v-for="(col, colIndex) in activeColumns"
          :key="`col-stream-${colIndex}`"
          class="relative border-l min-h-[600px] first:border-l-0 transition-colors duration-200"
          :class="isDark ? 'border-zinc-900/60 bg-zinc-900/[0.01]' : 'border-zinc-100 bg-white'"
        >
          <div
            v-for="hour in hours"
            :key="`hour-${colIndex}-${hour}`"
            class="h-[100px] sm:h-[120px] border-t first:border-t-0 transition-colors duration-150 cursor-crosshair"
            :class="isDark ? 'border-zinc-900/40 hover:bg-zinc-900/30' : 'border-zinc-100 hover:bg-zinc-50/60'"
            @click="createNewBookingSlot(col.id, hour)"
          ></div>

          <div
            v-for="event in col.events"
            :key="`event-${colIndex}-${event.id}`"
            @click="openEventSidebar(event)"
            class="absolute rounded-xl p-3 border w-[95%] mx-auto left-0 right-0 cursor-pointer z-10 overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            :style="getEventStyle(event)"
            :class="[
              getEventStatusClasses(event),
              isDark ? 'bg-zinc-900/95 backdrop-blur-md shadow-xl hover:border-zinc-700' : 'bg-white shadow-md hover:shadow-lg hover:border-zinc-300'
            ]"
          >
            <div class="space-y-1.5 h-full flex flex-col justify-between text-left">
              <div class="min-w-0">
                <div class="flex items-center justify-between gap-1">
                  <p class="text-xs font-semibold truncate tracking-wide m-0 transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-zinc-900'">{{ event.clientName }}</p>
                  
                  <div v-if="event.isSyncedFromGoogle" class="w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors duration-200 text-[9px] font-bold" 
                    :class="isDark ? 'bg-zinc-950 border-zinc-800 text-zinc-400' : 'bg-zinc-100 border-zinc-200 text-zinc-500'"
                    title="Calendrier Externe">
                    <span>G</span>
                  </div>
                </div>
                <p class="text-[11px] truncate font-medium mt-0.5 m-0 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ event.serviceName }}</p>
              </div>
              
              <div class="flex items-center justify-between gap-1 mt-1 pt-1.5 border-t shrink-0 text-[10px] transition-colors duration-200"
                :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
                <span class="font-semibold transition-colors duration-200"
                  :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ event.startTime }} - {{ event.endTime }}</span>
                <span class="px-2 py-0.5 rounded-full text-[9px] font-medium tracking-wide transition-colors duration-200" 
                  :class="event.status === 'Validé' 
                    ? (isDark ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-900/50' : 'bg-emerald-50 text-emerald-700') 
                    : (isDark ? 'bg-amber-950/60 text-amber-400 border border-amber-900/50' : 'bg-amber-50 text-amber-700')">
                  {{ event.status }}
                </span>
              </div>
            </div>
            <div class="w-full h-1 absolute bottom-0 left-0 cursor-ns-resize transition-colors duration-200"
              :class="isDark ? 'bg-zinc-800/80 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-300'"></div>
          </div>
        </div>

      </div>
    </div>

    <!-- Event Sidebar -->
    <EventSidebar
      :visible="sidebarVisible"
      :event="selectedEvent"
      @close="closeSidebar"
      @edit="handleEdit"
      @cancel="handleCancel"
      @remind="handleRemind"
      @payment="handlePayment"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import EventSidebar from '@/components/EventSidebar.vue'
import api from '@/api'
import { toast } from 'vue3-toastify'
import { useTheme } from '@/composables/useTheme'
import { usePermissions } from '@/composables/usePermissions';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const { isAdmin } = usePermissions();

// Router
const router = useRouter();

// Synchronisation Pipeline States

const nowIndicatorStyle = ref({ top: '38%' })
const nowLabel = ref('11:35')
const hours = ref(Array.from({ length: 12 }, (_, i) => i + 8)) // 08:00 to 19:00

// Event Sidebar States
const sidebarVisible = ref(false)
const selectedEvent = ref(null)
const fullEventData = ref(null)

// Calendar scroll container ref
const calendarScrollContainer = ref(null)

// View Mode Engine ('day-team' or 'week-solo')
const calendarViewMode = ref('week-solo')

// Current date tracking
const currentDate = ref(new Date())

// Target Staff Dataset Configuration
const employees = ref([])
const selectedEmployeeIds = ref([])
const activeWeekEmployeeId = ref(null)

// Calendar events from backend
const calendarEvents = ref([])

// Computed: Get week days with real dates
const weekDaysWithDates = computed(() => {
  const startOfWeek = getStartOfWeek(currentDate.value)
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek)
    date.setDate(startOfWeek.getDate() + i)
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
    const dayShort = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
    return {
      dayIndex: i + 1,
      label: dayNames[date.getDay()],
      dateCode: dayShort[date.getDay()],
      date: date,
      dateString: date.toISOString().split('T')[0],
      dayNumber: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear()
    }
  })
})

// Computed: Current period label
const currentPeriodLabel = computed(() => {
  if (calendarViewMode.value === 'week-solo') {
    const start = weekDaysWithDates.value[0]
    const end = weekDaysWithDates.value[6]
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    if (start.month === end.month) {
      return `${start.dayNumber}-${end.dayNumber} ${months[start.month]} ${start.year}`
    } else {
      return `${start.dayNumber} ${months[start.month]} - ${end.dayNumber} ${months[end.month]} ${end.year}`
    }
  } else {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
    return `${months[currentDate.value.getMonth()]} ${currentDate.value.getFullYear()}`
  }
})

// Helper: Get start of week (Monday)
const getStartOfWeek = (date) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

// Fetch staff from backend
const fetchStaff = async () => {
  try {
    const response = await api.staff.getAllStaff()
    const staffList = response.data?.administrateurs || []

    employees.value = staffList.map((staff, index) => ({
      id: staff._id,
      name: staff.nom,
      role: staff.specialite || 'Collaborateur',
      color: ['#e4e4e7', '#c4b5fd', '#fde047', '#fda4af', '#86efac'][index % 5]
    }))

    // Si collaborateur, filtrer uniquement son propre ID
    if (!isAdmin.value) {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const currentUserId = user.id || user._id

      // Ne sélectionner que son propre ID
      selectedEmployeeIds.value = [currentUserId]
      activeWeekEmployeeId.value = currentUserId
    } else {
      // Admin : sélectionner tous les employés
      selectedEmployeeIds.value = employees.value.map(e => e.id)
      if (employees.value.length > 0) {
        activeWeekEmployeeId.value = employees.value[0].id
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement du personnel', error)
    toast.error('Erreur lors du chargement du personnel')
  }
}

// Fetch appointments from backend
const fetchAppointments = async () => {
  try {
    const response = await api.appointments.getAllAppointments()
    let appointments = response.data || []
    console.log("rdv" , appointments)

    // Si collaborateur, filtrer uniquement ses propres rendez-vous
    if (!isAdmin.value) {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const currentUserId = user.id || user._id

      appointments = appointments.filter(apt => {
        const employeId = apt.employe?._id || apt.employe?.id || apt.employe
        return employeId === currentUserId
      })
    }

    // Get the range of dates to display
    const weekDays = weekDaysWithDates.value
    const startDateStr = weekDays[0].dateString
    const endDateStr = weekDays[6].dateString

    // Map appointments to calendar events format
    calendarEvents.value = appointments
      .filter(apt => {
        // Filter appointments within the current week
        const aptDateStr = new Date(apt.start).toISOString().split('T')[0]
        return aptDateStr >= startDateStr && aptDateStr <= endDateStr
      })
      .map(apt => {
        const startDate = new Date(apt.start)
        const endDate = new Date(apt.end)

        // Find which day of the week this appointment is on
        const aptDateStr = startDate.toISOString().split('T')[0]
        const dayIndexInWeek = weekDays.findIndex(d => d.dateString === aptDateStr) + 1

        // Calculate position and height based on time
        const startHour = startDate.getHours()
        const startMinute = startDate.getMinutes()
        const endHour = endDate.getHours()
        const endMinute = endDate.getMinutes()

        const topOffset = ((startHour - 8) * 100) + (startMinute / 60 * 100)
        const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute)
        const cardHeight = (duration / 60) * 100
        
        return {
          id: apt.id || apt._id,
          employeeId: apt.employe?.id || apt.employe?._id || null,
          dayIndex: dayIndexInWeek,
          dateString: aptDateStr,
          clientName: apt.nom,
          serviceName: apt.service?.map(s => s.nom).join(', ') || 'Service',
          startTime: `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`,
          endTime: `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`,
          topOffset: Math.max(0, topOffset),
          cardHeight: Math.max(50, cardHeight),
          status: apt.status || 'En attente',
          isSyncedFromGoogle: apt.metadata?.source === 'google_calendar' || !!apt.metadata?.googleEventId,
          metadata: apt.metadata,
          motif_annulation: apt.motif_annulation,
          note: apt.note,
          paymentAmount: apt.paymentAmount,
          montantAvantReduction: apt.montantAvantReduction,
          reductionAppliquee: apt.reductionAppliquee,
          detailsReduction: apt.detailsReduction,
          isPaidCompletely: apt.isPaidCompletely,
          // Conserver les données originales pour la sidebar
          originalData: apt

        }
        
      })
  } catch (error) {
    console.error('Erreur lors du chargement des rendez-vous', error)
    toast.error('Erreur lors du chargement des rendez-vous')
  }
}

// Navigation functions
const goToPreviousPeriod = () => {
  if (calendarViewMode.value === 'week-solo') {
    currentDate.value = new Date(currentDate.value.setDate(currentDate.value.getDate() - 7))
  } else {
    currentDate.value = new Date(currentDate.value.setDate(currentDate.value.getDate() - 1))
  }
  fetchAppointments()
}

const goToNextPeriod = () => {
  if (calendarViewMode.value === 'week-solo') {
    currentDate.value = new Date(currentDate.value.setDate(currentDate.value.getDate() + 7))
  } else {
    currentDate.value = new Date(currentDate.value.setDate(currentDate.value.getDate() + 1))
  }
  fetchAppointments()
}

const goToToday = () => {
  currentDate.value = new Date()
  fetchAppointments()
}

// Watch for view mode changes
watch(calendarViewMode, () => {
  fetchAppointments()
})

// Compute dynamically Columns Layout and Headers based on chosen layout Strategy
const activeColumns = computed(() => {
  if (calendarViewMode.value === 'day-team') {
    // Return columns representing each employee for today
    const todayStr = currentDate.value.toISOString().split('T')[0]
    return employees.value
      .filter(emp => selectedEmployeeIds.value.includes(emp.id))
      .map(emp => ({
        id: emp.id,
        title: emp.name,
        subtitle: emp.role,
        events: calendarEvents.value.filter(e => e.employeeId === emp.id && e.dateString === todayStr)
      }))
  } else {
    // Return columns representing the 7 days of the week for one designated single worker
    const targetWorker = employees.value.find(e => e.id === activeWeekEmployeeId.value)
    if (!targetWorker) return []

    return weekDaysWithDates.value.map(day => ({
      id: day.dayIndex,
      title: day.label,
      subtitle: `${day.dayNumber} ${day.dateCode}`,
      events: calendarEvents.value.filter(e => e.employeeId === targetWorker.id && e.dayIndex === day.dayIndex)
    }))
  }
})

// Calculates layout grid-columns count classes
const gridColsClass = computed(() => {
  const count = activeColumns.value.length
  return {
    'grid-cols-1': count === 1,
    'grid-cols-2': count === 2,
    'grid-cols-3': count === 3,
    'grid-cols-4': count === 4,
    'grid-cols-5': count === 5,
    'grid-cols-6': count === 6,
    'grid-cols-7': count === 7
  }
})

// Interactions Management Mechanics
const toggleEmployeeFilter = (id) => {
  const index = selectedEmployeeIds.value.indexOf(id)
  if (index > -1) {
    if (selectedEmployeeIds.value.length > 1) selectedEmployeeIds.value.splice(index, 1)
  } else {
    selectedEmployeeIds.value.push(id)
  }
}

const selectAllEmployees = () => {
  selectedEmployeeIds.value = employees.value.map(e => e.id)
}

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`
const getEventStyle = (event) => ({ top: `${event.topOffset}px`, height: `${event.cardHeight}px` })

const getEventStatusClasses = (event) => {
  if (event.isSyncedFromGoogle) {
    return isDark.value ? 'border-zinc-800 text-zinc-300' : 'border-zinc-200 text-zinc-500';
  }
  if (event.status === 'Validé') {
    return isDark.value ? 'border-emerald-950 text-emerald-400' : 'border-emerald-200/80 text-emerald-700';
  } else {
    return isDark.value ? 'border-amber-950 text-amber-400' : 'border-amber-200/80 text-amber-700';
  }
}



const createNewBookingSlot = (targetId, hour) => {
  console.log(`Open insertion module on segment reference ID: ${targetId} at ${hour}:00`)
}

// ========================================
// SIDEBAR EVENT MANAGEMENT
// ========================================

// Ouvrir la sidebar avec les détails de l'événement
const openEventSidebar = async (event) => {
  try {
    // Récupérer les données complètes du rendez-vous depuis le backend
    const response = await api.appointments.getAppointmentById(event.id)
    fullEventData.value = response.data

    // Conserver le flag isSyncedFromGoogle depuis l'événement du calendrier
    selectedEvent.value = {
      ...fullEventData.value,
      isSyncedFromGoogle: event.isSyncedFromGoogle,
      startTime: event.startTime,
      endTime: event.endTime
    }
    sidebarVisible.value = true
  } catch (error) {
    console.error('Erreur lors du chargement de l\'événement:', error)
    // Fallback: utiliser les données du calendrier si le backend échoue
    // Utiliser originalData si disponible, sinon l'événement lui-même
    selectedEvent.value = event.originalData || event
    sidebarVisible.value = true
  }
}

// Fermer la sidebar
const closeSidebar = () => {
  sidebarVisible.value = false
  selectedEvent.value = null
  fullEventData.value = null
}

// Gérer la modification d'un événement
const handleEdit = (event) => {
  const appointmentId = event.id || event._id
  closeSidebar()
  router.push(`/appointments/edit/${appointmentId}`)
}

// Gérer l'annulation d'un événement
const handleCancel = async (event) => {
  // Vérifier si c'est un événement Google
  if (event.isSyncedFromGoogle || event.metadata?.source === 'google_calendar') {
    toast.warning('Impossible d\'annuler un événement Google Calendar depuis BookiPro. Veuillez le modifier dans Google Calendar.')
    return
  }

  if (confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) {
    try {
      await api.appointments.cancelAppointment(event.id || event._id)
      toast.success('Rendez-vous annulé avec succès')
      closeSidebar()
      await fetchAppointments()
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error)
      toast.error('Erreur lors de l\'annulation du rendez-vous')
    }
  }
}

// Gérer l'envoi d'un rappel
const handleRemind = async (event) => {
  try {
    const response = await api.appointments.sendReminder(event.id || event._id)
    const data = response.data || {}

    // Construire le message selon les résultats
    if (data.emailSent && data.smsSent) {
      toast.success('Rappel envoyé par email et SMS')
    } else if (data.emailSent && !data.smsSent) {
      if (data.smsDisabled) {
        toast.success('Rappel envoyé par email (SMS désactivé)')
      } else if (data.noPhone) {
        toast.success('Rappel envoyé par email (pas de téléphone)')
      } else {
        toast.warning('Rappel envoyé par email uniquement (SMS échoué)')
      }
    } else if (data.emailSent) {
      toast.success('Rappel envoyé par email')
    } else {
      toast.warning('Rappel envoyé avec des limitations')
    }
  } catch (error) {
    console.error('Erreur lors de l\'envoi du rappel:', error)
    toast.error('Erreur lors de l\'envoi du rappel')
  }
}

// Gérer le paiement
const handlePayment = (event) => {
  closeSidebar()

  // Préparer les données pour la caisse
  const services = event.service || []

  router.push({
    path: '/caisse',
    query: {
      clientId: event.client?._id || event.client?.id,
      clientName: event.nom || event.clientName,
      appointmentId: event.id || event._id,
      services: JSON.stringify(services)  // Passer les services en query param JSON
    }
  })
}

// ========================================
// DATE & TIME UTILITIES
// ========================================

// Formater la date complète (ex: "Lundi 11 juin 2026")
const formatFullDate = (date) => {
  const d = new Date(date)
  const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

// Mettre à jour la position de la barre d'indication de l'heure actuelle
const updateNowIndicator = () => {
  const now = new Date()
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()

  // Ne montrer la barre que pendant les heures ouvrables (8h-20h)
  if (currentHour >= 8 && currentHour < 20) {
    // Calculer la position en pixels (100px par heure dans notre grille)
    const hourOffset = (currentHour - 8) * 100 // 100px par heure en mode mobile, 120px en desktop
    const minuteOffset = (currentMinute / 60) * 100
    const totalOffset = hourOffset + minuteOffset

    nowIndicatorStyle.value = { top: `${totalOffset}px` }
    nowLabel.value = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`
  }
}

// Interval pour mettre à jour l'heure toutes les minutes
let nowIndicatorInterval = null

// Initial data load
onMounted(async () => {
  await fetchStaff()
  await fetchAppointments()

  // Initialiser la barre d'heure
  updateNowIndicator()

  // Scroll automatique vers l'heure actuelle (centré dans la vue)
  if (calendarScrollContainer.value) {
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()

    if (currentHour >= 8 && currentHour < 20) {
      // Calculer la position de l'heure actuelle (100px par heure)
      const hourOffset = (currentHour - 8) * 100
      const minuteOffset = (currentMinute / 60) * 100
      const totalOffset = hourOffset + minuteOffset

      // Centrer l'heure actuelle dans la vue
      const containerHeight = calendarScrollContainer.value.clientHeight
      const scrollPosition = totalOffset - (containerHeight / 2) + 50 // +50 pour un meilleur centrage visuel

      // Attendre un micro-délai pour s'assurer que le DOM est complètement rendu
      setTimeout(() => {
        calendarScrollContainer.value.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        })
      }, 100)
    }
  }

  // Mettre à jour la barre toutes les 30 secondes
  nowIndicatorInterval = setInterval(updateNowIndicator, 30000)
})

// Nettoyer l'interval au démontage
onUnmounted(() => {
  if (nowIndicatorInterval) {
    clearInterval(nowIndicatorInterval)
  }
})
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>